import { Box, SvgIcon, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import PropertyIcon from '@hapana/shared/web/styles/assets/svgs/tag.svg'
import NoteIcon from '@hapana/shared/web/styles/assets/svgs/note.svg'

import ModalShowAllNotes from './modals/modal-show-all-notes.component'
import ModalShowAllProperties from './modals/modal-show-all-properties.component'
import NoteAlert from './note-alert.component'
import { NOTE_ALERT_TYPES } from './notes-properties.constants'
import { styles } from './notes-properties.styles'
import PropertyAlert from './property-alert.component'

const NotesType = ({ type, alerts, username }) => {
  const { pushDialog } = useDialog()
  const { t } = useTranslation(['checkIn'])
  const isNote = type === NOTE_ALERT_TYPES.NOTE
  const isProperty = type === NOTE_ALERT_TYPES.PROPERTIES

  const handleViewAll = useCallback(() => {
    if (isNote) {
      pushDialog(ModalShowAllNotes, {
        alerts,
        username,
      })
    } else {
      pushDialog(ModalShowAllProperties, {
        alerts,
        username,
      })
    }
  }, [alerts, isNote, pushDialog, username])

  const renderSlicedAlertTypes = () => {
    const orderedAlertsByDate = [...alerts].sort((a, b) => new Date(b.date) - new Date(a.date))

    if (isNote) {
      return orderedAlertsByDate.map((o) => (
        <NoteAlert key={o.id} date={o.date} description={o.text} />
      ))
    }
    if (isProperty) {
      return orderedAlertsByDate.map((o) => (
        <PropertyAlert key={o.id} title={o.title} description={o.value} />
      ))
    }
    return null
  }

  const renderEmptyState = () => {
    return (
      <>
        <Box sx={styles.noteTypeWrapper}>
          <Box sx={styles.notesTypeContainer}>
            <Typography typography="header4" color="gray.80">
              {isNote
                ? t('checkIn:alerts.notesAndPropertiesAlerts.notes')
                : t('checkIn:alerts.notesAndPropertiesAlerts.properties')}
            </Typography>
            <Typography typography="header4" color="gray.40" sx={styles.dot}>
              •
            </Typography>
            <Typography typography="medium" color="gray.40">
              {t('checkIn:alerts.noAlerts')}
            </Typography>
          </Box>
        </Box>
        <Box sx={styles.emptyStateContainer}>
          <Box item>
            <SvgIcon component={isNote ? NoteIcon : PropertyIcon} sx={styles.emptyNotesIcon} />
          </Box>
          <Box>
            <Typography typography="medium" color="gray.40" mb={1.25}>
              {isNote ? t('checkIn:alerts.emptyNotes') : t('checkIn:alerts.emptyProperties')}
            </Typography>
          </Box>
        </Box>
      </>
    )
  }

  if (alerts.length === 0) {
    return renderEmptyState()
  }

  return (
    <>
      <Box sx={styles.noteTypeWrapper}>
        <Box sx={styles.notesTypeContainer}>
          <Typography typography="header4" color="gray.80">
            {isNote
              ? t('checkIn:alerts.notesAndPropertiesAlerts.notes')
              : t('checkIn:alerts.notesAndPropertiesAlerts.properties')}
          </Typography>
          <Typography typography="header4" color="gray.40" sx={styles.dot}>
            •
          </Typography>
          <Typography typography="header4" color="gray.64">
            {t('checkIn:alerts.numberOfAlerts', {
              count: alerts.length,
            })}
          </Typography>
        </Box>
        <Box>
          <Typography typography="header4" sx={styles.viewAllType} onClick={handleViewAll}>
            {isNote ? t('checkIn:alerts.viewAllNotes') : t('checkIn:alerts.viewAllProperties')}
          </Typography>
        </Box>
      </Box>
      <Box>{renderSlicedAlertTypes()}</Box>
    </>
  )
}

export default NotesType

NotesType.propTypes = {
  type: PropTypes.oneOf(Object.values(NOTE_ALERT_TYPES)),
  alerts: PropTypes.array,
  username: PropTypes.string,
}

NotesType.defaultProps = {
  alerts: [],
  type: NOTE_ALERT_TYPES.NOTE,
  username: '',
}
